import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["totalPages", "currentPage", "onClickFunction", "getPageUrl", "position", "maxToShow", "className", "data-test"];
import React from 'react';
import PropTypes from 'prop-types';
import { ChevronIcon } from '@sainsburys-tech/bolt-icons';
import * as Styled from './Pagination.styles';

var Pagination = function Pagination(_ref) {
  var totalPages = _ref.totalPages,
      currentPage = _ref.currentPage,
      onClickFunction = _ref.onClickFunction,
      getPageUrl = _ref.getPageUrl,
      position = _ref.position,
      maxToShow = _ref.maxToShow,
      className = _ref.className,
      dataTest = _ref['data-test'],
      props = _objectWithoutProperties(_ref, _excluded);

  var showPreviousEllipsis = false;
  var showNextEllipsis = false;
  var pages = [];

  var totalPagesText = function totalPagesText(current, total) {
    return "Page ".concat(current, " of ").concat(total);
  };

  if (totalPages === 1) {
    return /*#__PURE__*/React.createElement(Styled.Pagination, _extends({
      className: "".concat(className, " xs-row"),
      "data-test": dataTest,
      role: "navigation",
      "aria-label": "Pagination Navigation"
    }, props), /*#__PURE__*/React.createElement(Styled.PageNumbers, {
      role: "link",
      className: "xs-row",
      "data-test": "".concat(dataTest, "-numbers"),
      "aria-label": totalPagesText(1, 1)
    }, totalPagesText(1, 1)));
  }

  var pagesRaw = Array.from({
    length: totalPages
  }, function (value, number) {
    return number + 1;
  }); // First set of pages e.g. the first 4 if we show a max of 5

  if (totalPages <= maxToShow) {
    pages = pagesRaw.slice(1, totalPages - 1);
    showPreviousEllipsis = false;
    showNextEllipsis = false;
  } else if (currentPage <= Math.round(maxToShow / 2)) {
    pages = _toConsumableArray(pagesRaw.slice(1, maxToShow - 1));
    showPreviousEllipsis = false;
    showNextEllipsis = true;
  } else if (currentPage > totalPages - maxToShow / 2) {
    pages = _toConsumableArray(pagesRaw.slice(-1 * (maxToShow - 1), -1));
    showPreviousEllipsis = true;
    showNextEllipsis = false;
  } else if (currentPage > maxToShow / 2) {
    var totalToShow = maxToShow - 3;
    var sliceBefore = currentPage - (totalToShow / 2 + 1);
    var sliceAfter = currentPage + totalToShow / 2;
    pages = _toConsumableArray(pagesRaw.slice(sliceBefore, sliceAfter));
    showPreviousEllipsis = true;
    showNextEllipsis = true;
  }

  var hasGetPageUrl = !!getPageUrl();

  var handleOnClick = function handleOnClick(clickEvent, pageNumber) {
    if (!hasGetPageUrl) clickEvent.preventDefault();
    onClickFunction(clickEvent, pageNumber);
  };

  var first = function first(clickEvent) {
    return handleOnClick(clickEvent, 1);
  };

  var last = function last(clickEvent) {
    return handleOnClick(clickEvent, totalPages);
  };

  var prevEllipsis = function prevEllipsis(clickEvent) {
    return handleOnClick(clickEvent, pages[0] - 1);
  };

  var nextEllipsis = function nextEllipsis(clickEvent) {
    return handleOnClick(clickEvent, pages.slice(-1)[0] + 1);
  };

  var prevPage = function prevPage(clickEvent) {
    if (currentPage !== 1) handleOnClick(clickEvent, currentPage - 1);
  };

  var nextPage = function nextPage(clickEvent) {
    if (currentPage !== totalPages) handleOnClick(clickEvent, currentPage + 1);
  };

  var pageLinks = Array.from(pages).map(function (pageNumber) {
    var onClick = function onClick(clickEvent) {
      return handleOnClick(clickEvent, pageNumber);
    };

    return /*#__PURE__*/React.createElement(Styled.PageLink, {
      onClick: onClick,
      active: pageNumber === currentPage,
      easter: pageNumber === 22,
      className: "xs-hidden sm-row",
      "data-test": "".concat(dataTest, "-page-").concat(pageNumber),
      key: "pagination-".concat(pageNumber),
      "aria-label": "".concat(currentPage === pageNumber ? 'Current page,' : 'Go to', " page ").concat(pageNumber),
      "aria-current": pageNumber === currentPage,
      href: getPageUrl(pageNumber),
      role: "link"
    }, pageNumber);
  });
  return /*#__PURE__*/React.createElement(Styled.Pagination, _extends({
    role: "navigation",
    "aria-label": "Pagination Navigation",
    className: "".concat(className, " xs-row"),
    position: position,
    "data-test": dataTest
  }, props), /*#__PURE__*/React.createElement(Styled.PageLink, {
    as: currentPage !== 1 ? 'a' : 'div',
    onClick: prevPage,
    disabled: currentPage === 1,
    className: "xs-row",
    "data-test": "".concat(dataTest, "-arrow-left"),
    "aria-hidden": currentPage === 1,
    "aria-label": "Go to page ".concat(currentPage - 1),
    href: currentPage !== 1 ? getPageUrl(currentPage - 1) : null,
    role: "link"
  }, /*#__PURE__*/React.createElement(ChevronIcon, {
    dir: "left",
    width: "24",
    height: "24"
  })), /*#__PURE__*/React.createElement(Styled.PageLink, {
    onClick: first,
    active: currentPage === 1,
    className: "xs-hidden sm-row",
    "data-test": "".concat(dataTest, "-page-1"),
    key: "pagination-1",
    "aria-label": "".concat(currentPage === 1 ? 'Current page,' : 'Go to', " page 1"),
    "aria-current": currentPage === 1,
    href: getPageUrl(1),
    role: "link"
  }, 1), showPreviousEllipsis && /*#__PURE__*/React.createElement(Styled.PageLink, {
    onClick: prevEllipsis,
    active: false,
    className: "xs-hidden sm-row",
    "data-test": "".concat(dataTest, "-ellipsis-prev"),
    key: "pagination-prev-ellipsis",
    "aria-label": "Go to page ".concat(pages[0] - 1),
    href: getPageUrl(pages[0] - 1),
    role: "link"
  }, "..."), pageLinks, /*#__PURE__*/React.createElement(Styled.PageNumbers, {
    className: "xs-row sm-hidden",
    "data-test": "".concat(dataTest, "-numbers")
  }, totalPagesText(currentPage, totalPages)), showNextEllipsis && /*#__PURE__*/React.createElement(Styled.PageLink, {
    onClick: nextEllipsis,
    active: false,
    className: "xs-hidden sm-row",
    "data-test": "".concat(dataTest, "-ellipsis-next"),
    key: "pagination-next-ellipsis",
    "aria-label": "Go to page ".concat(pages.slice(-1)[0] + 1),
    href: getPageUrl(pages.slice(-1)[0] + 1),
    role: "link"
  }, "..."), /*#__PURE__*/React.createElement(Styled.PageLink, {
    onClick: last,
    active: totalPages === currentPage,
    easter: totalPages === 22,
    className: "xs-hidden sm-row",
    "data-test": "".concat(dataTest, "-page-").concat(totalPages),
    key: "pagination-".concat(totalPages),
    "aria-label": "".concat(currentPage === totalPages ? 'Current page,' : 'Go to', " page ").concat(totalPages),
    "aria-current": currentPage === totalPages,
    href: getPageUrl(totalPages),
    role: "link"
  }, totalPages), /*#__PURE__*/React.createElement(Styled.PageLink, {
    as: currentPage !== totalPages ? 'a' : 'div',
    onClick: nextPage,
    disabled: currentPage === totalPages,
    className: "xs-row",
    "data-test": "".concat(dataTest, "-arrow-right"),
    "aria-label": "Go to page ".concat(currentPage + 1),
    href: currentPage === totalPages ? null : getPageUrl(currentPage + 1),
    role: "link"
  }, /*#__PURE__*/React.createElement(ChevronIcon, {
    dir: "right",
    width: "24",
    height: "24"
  })));
};

Pagination.propTypes = {
  /**
   * Required total number of pages
   */
  totalPages: PropTypes.number.isRequired,

  /**
   * Required current page
   */
  currentPage: PropTypes.number.isRequired,

  /**
   * Function to be called when pagination is clicked, to take targetPage and event as arguments
   */
  onClickFunction: PropTypes.func,

  /**
   * Function to set the href for a pagination link, to take targetPage as argument
   */
  getPageUrl: PropTypes.func,

  /**
   * Optional position string, to specify whether the pagination should be right, centre or left position
   */
  position: PropTypes.oneOf(['left', 'center', 'right']),

  /**
   * Optional maximum number of pages to show (excluding ellipses)
   */
  maxToShow: PropTypes.number,

  /**
   * Specify an optional className to be added to your Pagination
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Pagination for e2e testing
   */
  'data-test': PropTypes.string
};
Pagination.defaultProps = {
  position: 'right',
  maxToShow: 5,
  className: '',
  'data-test': 'component-pagination',
  onClickFunction: function onClickFunction() {},
  getPageUrl: function getPageUrl() {}
};
export default Pagination;